/**
 * Quick order pad — A. Titan Instruments
 * ---------------------------------------------------------------------------
 * Resolves typed or pasted item numbers to real variants, shows the buyer what
 * each line actually is, and adds the lot to the cart in one action.
 *
 * Item numbers are resolved through the `quick-order-lookup` section fetched over
 * Shopify's Section Rendering API. /search/suggest.json does not return variant
 * SKUs, so it cannot say which variant of a multi-variant product carries the item
 * number that was typed. The section can, and needs no app.
 *
 * No dependencies, no CDN. Loaded with `defer` by sections/quick-order-pad.liquid.
 */

(function () {
  'use strict';

  var LOOKUP_SECTION = 'quick-order-lookup';
  var MAX_CONCURRENT_LOOKUPS = 4;
  var MAX_SUGGESTIONS = 3;
  var MAX_PASTE_LINES = 200;

  /* ------------------------------------------------------------------ utils */

  /**
   * ITEM NUMBER MATCHING — see scripts/titan-migration/lib/sku-match.mjs for the full
   * reasoning and outputs/titan-shopify/SKU-MATCHING.md for the decision record. The tiers
   * below are the ES5 mirror of tiers 0-2 of that module (theme assets cannot import), and
   * scripts/titan-migration/test/sku-match.test.mjs is the specification both sides meet.
   *
   * In A. Titan part numbers, punctuation is load-bearing:
   *
   *   150+  Upper Universal PLUS Series forceps   vs   150  Standard Upper Universal forceps
   *   151+  the lower-jaw equivalent              vs   151
   *   P-3   Prichard Periosteal Elevator          vs   P3   Angled Periotome Tip
   *
   * Anything that strips `+` or `-` before comparing will sell a dealer the wrong
   * instrument. So nothing is stripped. Only two things are folded:
   *
   *   canonical  — case, and runs of whitespace ("h5  dl" == "H5 DL", "will-23" == "WILL-23")
   *   separator  — separator STYLE, so "WILL 23 UL" == "Will-23 UL" and "LUX 2" == "Lux_2".
   *                Never separator PRESENCE: "P3" still cannot reach "P-3".
   *
   * The tiers are tried in order and the first one with ANY candidate decides. If that tier
   * finds MORE THAN ONE variant, the answer is "I cannot tell" — the buyer is shown the
   * candidates and picks. The store really does carry ten SKU spellings that collide (two
   * variants both spelt `2S`, two both spelt `707`, `XT 135` twice), and the previous code
   * silently took whichever the search happened to return first.
   */
  function canonicalSku(value) {
    return String(value == null ? '' : value).trim().replace(/\s+/g, ' ').toUpperCase();
  }

  function separatorSku(value) {
    return canonicalSku(value).replace(/[ \-_]+/g, '-');
  }

  /**
   * Cache key — the RAW trimmed term, deliberately not a folded one.
   *
   * `WILL-23` and `Will-23` are two different live variants that each match themselves
   * exactly. Caching them under one folded key would serve whichever was typed first to
   * both, which is the same class of bug as folding the `+` off `150+`. Repeated identical
   * lines in a paste — the case the cache exists for — still share a slot.
   */
  function cacheKey(value) {
    return String(value == null ? '' : value).trim();
  }

  /**
   * Pick the one variant a typed item number means, or refuse.
   *
   * Returns { variant, candidates } — `variant` is null when nothing matched OR when more
   * than one matched, and `candidates` holds what it could not choose between.
   */
  function resolveVariants(variants, term) {
    var wanted = String(term == null ? '' : term).trim();
    if (!wanted) return { variant: null, candidates: [] };

    var tiers = [
      function (s) { return String(s == null ? '' : s).trim(); },
      canonicalSku,
      separatorSku
    ];

    for (var t = 0; t < tiers.length; t++) {
      var key = tiers[t](wanted);
      if (!key) continue;

      var hits = [];
      for (var i = 0; i < variants.length; i++) {
        var sku = variants[i] && variants[i].sku;
        if (!sku) continue;
        if (tiers[t](sku) === key) hits.push(variants[i]);
      }

      if (hits.length === 1) return { variant: hits[0], candidates: hits };
      if (hits.length > 1) return { variant: null, candidates: hits };
    }

    return { variant: null, candidates: [] };
  }

  /** Shopify money format, replicated so the pad can total lines client-side. */
  function formatMoney(cents, format) {
    var value = Number(cents);
    if (!isFinite(value)) return '';

    var fmt = format || (window.theme && window.theme.moneyFormat) || '${{amount}}';

    function withCommas(number) {
      return number.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    }

    function decimals(number, precision, thousands, decimal) {
      var amount = (number / 100.0).toFixed(precision);
      var parts = amount.split('.');
      return withCommas(parts[0]).replace(/,/g, thousands) + (parts[1] ? decimal + parts[1] : '');
    }

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, placeholder) {
      switch (placeholder) {
        case 'amount': return decimals(value, 2, ',', '.');
        case 'amount_no_decimals': return decimals(value, 0, ',', '.');
        case 'amount_with_comma_separator': return decimals(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return decimals(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return decimals(value, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return decimals(value, 0, ' ', '.');
        case 'amount_with_space_separator': return decimals(value, 2, ' ', ',');
        default: return match;
      }
    });
  }

  function parseJsonAttribute(element, attribute, fallback) {
    var raw = element.getAttribute(attribute);
    if (!raw) return fallback;
    try {
      return JSON.parse(raw);
    } catch (error) {
      return fallback;
    }
  }

  /* --------------------------------------------------------------- lookup */

  /**
   * One lookup client per pad. Caches by normalised item number (a paste of 60
   * lines with repeats costs one request per distinct number) and never runs more
   * than MAX_CONCURRENT_LOOKUPS requests at a time, so pasting a long list does
   * not open sixty sockets at once.
   */
  function LookupClient(searchUrl) {
    this.searchUrl = searchUrl;
    this.cache = Object.create(null);
    this.queue = [];
    this.inFlight = 0;
  }

  LookupClient.prototype.lookup = function (rawSku) {
    var key = cacheKey(rawSku);
    if (!key) return Promise.resolve({ ok: true, exact: null, ambiguous: false, suggestions: [] });
    if (this.cache[key]) return this.cache[key];

    var self = this;
    var promise = new Promise(function (resolve) {
      self.queue.push({ key: key, term: String(rawSku).trim(), resolve: resolve });
      self._drain();
    });

    this.cache[key] = promise;
    return promise;
  };

  LookupClient.prototype._drain = function () {
    var self = this;
    while (this.inFlight < MAX_CONCURRENT_LOOKUPS && this.queue.length > 0) {
      var job = this.queue.shift();
      this.inFlight += 1;
      this._request(job.term)
        .then(function (result) { job.resolve(result); })
        .catch(function () { job.resolve({ ok: false, exact: null, ambiguous: false, suggestions: [] }); })
        .then(function () {
          self.inFlight -= 1;
          self._drain();
        });
    }
  };

  LookupClient.prototype._request = function (term) {
    var url = new URL(this.searchUrl, window.location.origin);
    url.searchParams.set('section_id', LOOKUP_SECTION);
    url.searchParams.set('type', 'product');
    // Restrict the searched field to the SKU. A bare number like "63" is both a
    // real item number and a substring of dozens of titles; searching titles too
    // would bury the item the buyer actually asked for behind the noise.
    url.searchParams.set('options[fields]', 'variants.sku');
    // Prefix matching only widens the candidate pool — the match below is exact.
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('options[unavailable_products]', 'last');
    url.searchParams.set('q', term);

    return fetch(url.toString(), { credentials: 'same-origin', method: 'GET' })
      .then(function (response) {
        if (!response.ok) throw new Error('Lookup responded ' + response.status);
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var node = doc.querySelector('script.titan-lookup-data');
        if (!node) throw new Error('Lookup section missing from response');

        var payload = JSON.parse(node.textContent);
        var variants = Array.isArray(payload.variants) ? payload.variants : [];

        var resolved = resolveVariants(variants, term);

        // More than one candidate at the winning tier means the item number the buyer typed
        // does not identify one instrument. Offer the candidates; never pick for them.
        if (!resolved.variant && resolved.candidates.length > 1) {
          return { ok: true, exact: null, ambiguous: true, suggestions: resolved.candidates.slice(0, MAX_SUGGESTIONS) };
        }

        if (resolved.variant) {
          return { ok: true, exact: resolved.variant, ambiguous: false, suggestions: [] };
        }

        // Nothing matched. Everything the search returned becomes a "did you mean".
        var suggestions = [];
        for (var i = 0; i < variants.length && suggestions.length < MAX_SUGGESTIONS; i++) {
          if (variants[i] && variants[i].sku) suggestions.push(variants[i]);
        }

        return { ok: true, exact: null, ambiguous: false, suggestions: suggestions };
      });
  };

  /* ------------------------------------------------------------------- pad */

  function QuickOrderPad(element) {
    this.element = element;
    this.strings = parseJsonAttribute(element, 'data-strings', {});
    this.lookup = new LookupClient(element.getAttribute('data-lookup-url') || '/search');
    this.cartAddUrl = element.getAttribute('data-cart-add-url') || '/cart/add';
    this.cartUrl = element.getAttribute('data-cart-url') || '/cart';

    this.rowsContainer = element.querySelector('[data-quick-order-rows]');
    this.rowTemplate = element.querySelector('[data-quick-order-row-template]');
    this.summaryElement = element.querySelector('[data-quick-order-summary]');
    this.reportElement = element.querySelector('[data-quick-order-report]');
    this.submitButton = element.querySelector('[data-quick-order-submit]');
    this.pasteField = element.querySelector('[data-quick-order-paste]');

    this.rowState = new WeakMap();
    this.rowSequence = this.rowsContainer.querySelectorAll('[data-quick-order-row]').length;

    this._attachListeners();
    this._refreshSummary();
  }

  QuickOrderPad.prototype.string = function (key, replacements) {
    var value = this.strings[key];
    if (typeof value !== 'string') return '';
    if (!replacements) return value;
    Object.keys(replacements).forEach(function (token) {
      value = value.split(token).join(replacements[token]);
    });
    return value;
  };

  QuickOrderPad.prototype._attachListeners = function () {
    var self = this;

    this.element.addEventListener('input', function (event) {
      if (event.target.matches('[data-quick-order-item]')) {
        self._scheduleResolve(event.target.closest('[data-quick-order-row]'));
      } else if (event.target.matches('[data-quick-order-qty]')) {
        self._renderRow(event.target.closest('[data-quick-order-row]'));
        self._refreshSummary();
      }
    });

    // A resolve on blur catches paste-into-a-single-field and autofill, which do
    // not always produce the input events the debounce above listens for.
    this.element.addEventListener('focusout', function (event) {
      if (event.target.matches('[data-quick-order-item]')) {
        self._resolveRow(event.target.closest('[data-quick-order-row]'));
      }
    });

    this.element.addEventListener('click', function (event) {
      var remove = event.target.closest('[data-quick-order-remove]');
      if (remove) {
        event.preventDefault();
        self._removeRow(remove.closest('[data-quick-order-row]'));
        return;
      }

      var suggestion = event.target.closest('[data-quick-order-suggestion]');
      if (suggestion) {
        event.preventDefault();
        var row = suggestion.closest('[data-quick-order-row]');
        var input = row.querySelector('[data-quick-order-item]');

        // An ambiguous line is a choice between candidates that may share the SAME sku
        // string (the store carries two variants spelt `2S`, two spelt `707`, two spelt
        // `XT 135`). Re-typing the sku would just be ambiguous again, so the pick is taken
        // directly by position instead of going back through the lookup.
        var pick = suggestion.getAttribute('data-pick');
        var pickState = self.rowState.get(row);
        if (pick !== null && pickState && pickState.status === 'ambiguous') {
          var chosen = (pickState.suggestions || [])[parseInt(pick, 10)];
          if (chosen) {
            pickState.status = 'resolved';
            pickState.variant = chosen;
            pickState.suggestions = [];
            pickState.token = (pickState.token || 0) + 1; // orphan any lookup still in flight
            self.rowState.set(row, pickState);
            input.focus();
            self._renderRow(row);
            self._refreshSummary();
            return;
          }
        }

        input.value = suggestion.getAttribute('data-sku');
        input.focus();
        self._resolveRow(row);
        return;
      }

      if (event.target.closest('[data-quick-order-add-row]')) {
        event.preventDefault();
        self._addRow(true);
        return;
      }

      if (event.target.closest('[data-quick-order-clear]')) {
        event.preventDefault();
        self._clear();
        return;
      }

      if (event.target.closest('[data-quick-order-paste-submit]')) {
        event.preventDefault();
        self._applyPaste();
        return;
      }

      if (event.target.closest('[data-quick-order-submit]')) {
        event.preventDefault();
        self._submit();
      }
    });

    // Keyboard-only operation: Enter in the item field moves to the quantity,
    // Enter in the quantity opens the next line (creating one at the end).
    this.element.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;

      var row = event.target.closest('[data-quick-order-row]');
      if (!row) return;

      if (event.target.matches('[data-quick-order-item]')) {
        event.preventDefault();
        row.querySelector('[data-quick-order-qty]').focus();
      } else if (event.target.matches('[data-quick-order-qty]')) {
        event.preventDefault();
        var next = row.nextElementSibling;
        if (!next) next = self._addRow(false);
        next.querySelector('[data-quick-order-item]').focus();
      }
    });
  };

  QuickOrderPad.prototype._rows = function () {
    return Array.prototype.slice.call(this.rowsContainer.querySelectorAll('[data-quick-order-row]'));
  };

  QuickOrderPad.prototype._addRow = function (focus) {
    var fragment = this.rowTemplate.content.cloneNode(true);
    var row = fragment.querySelector('[data-quick-order-row]');
    this.rowSequence += 1;
    var index = this.rowSequence;

    var itemInput = row.querySelector('[data-quick-order-item]');
    var qtyInput = row.querySelector('[data-quick-order-qty]');
    var itemLabel = row.querySelector('[data-quick-order-item-label]');
    var qtyLabel = row.querySelector('[data-quick-order-qty-label]');
    var removeLabel = row.querySelector('[data-quick-order-remove-label]');

    itemInput.id = 'quick-order-item-' + index;
    qtyInput.id = 'quick-order-qty-' + index;
    itemLabel.setAttribute('for', itemInput.id);
    itemLabel.textContent = this.string('rowLabel') + ' ' + index;
    qtyLabel.setAttribute('for', qtyInput.id);
    qtyLabel.textContent = this.string('qtyLabel') + ' ' + index;
    removeLabel.textContent = this.string('removeLabel') + ' ' + index;

    this.rowsContainer.appendChild(row);
    if (focus) itemInput.focus();
    this._refreshSummary();
    return row;
  };

  QuickOrderPad.prototype._removeRow = function (row) {
    if (!row) return;
    var rows = this._rows();
    var position = rows.indexOf(row);

    if (rows.length <= 1) {
      // Never leave the pad with nothing to type into — empty the row instead.
      row.querySelector('[data-quick-order-item]').value = '';
      row.querySelector('[data-quick-order-qty]').value = '1';
      this.rowState.delete(row);
      this._renderRow(row);
      this._refreshSummary();
      return;
    }

    this.rowState.delete(row);
    row.remove();

    var focusTarget = rows[position + 1] || rows[position - 1];
    if (focusTarget) focusTarget.querySelector('[data-quick-order-item]').focus();
    this._refreshSummary();
  };

  QuickOrderPad.prototype._clear = function () {
    var self = this;
    var rows = this._rows();
    rows.forEach(function (row, index) {
      if (index === 0) {
        row.querySelector('[data-quick-order-item]').value = '';
        row.querySelector('[data-quick-order-qty]').value = '1';
        self.rowState.delete(row);
        self._renderRow(row);
      } else {
        self.rowState.delete(row);
        row.remove();
      }
    });
    if (this.pasteField) this.pasteField.value = '';
    this._setReport(null);
    this._refreshSummary();
    var first = this._rows()[0];
    if (first) first.querySelector('[data-quick-order-item]').focus();
  };

  QuickOrderPad.prototype._scheduleResolve = function (row) {
    var self = this;
    if (!row) return;
    var state = this.rowState.get(row) || {};
    clearTimeout(state.debounce);
    state.debounce = setTimeout(function () { self._resolveRow(row); }, 350);
    this.rowState.set(row, state);
  };

  QuickOrderPad.prototype._resolveRow = function (row) {
    if (!row) return Promise.resolve();

    var self = this;
    var input = row.querySelector('[data-quick-order-item]');
    var term = input.value.trim();
    var state = this.rowState.get(row) || {};
    clearTimeout(state.debounce);

    if (!term) {
      state.status = 'empty';
      state.variant = null;
      state.term = '';
      this.rowState.set(row, state);
      this._renderRow(row);
      this._refreshSummary();
      return Promise.resolve();
    }

    if (state.status === 'resolved' && state.term === term) {
      return Promise.resolve();
    }
    if (state.status === 'notfound' && state.term === term) {
      return Promise.resolve();
    }

    state.term = term;
    state.status = 'pending';
    state.variant = null;
    state.token = (state.token || 0) + 1;
    var token = state.token;
    this.rowState.set(row, state);
    this._renderRow(row);
    this._refreshSummary();

    return this.lookup.lookup(term).then(function (result) {
      var current = self.rowState.get(row);
      // The row moved on (retyped, removed, or replaced by a suggestion).
      if (!current || current.token !== token) return;

      if (!result.ok) {
        current.status = 'error';
      } else if (result.exact) {
        current.status = 'resolved';
        current.variant = result.exact;
        current.suggestions = [];
      } else if (result.ambiguous) {
        // Two or more live variants answer to this item number. The line stays unresolved
        // and cannot be added to the cart until the buyer picks one.
        current.status = 'ambiguous';
        current.suggestions = result.suggestions || [];
      } else {
        current.status = 'notfound';
        current.suggestions = result.suggestions || [];
      }

      self.rowState.set(row, current);
      self._renderRow(row);
      self._refreshSummary();
    });
  };

  QuickOrderPad.prototype._quantity = function (row) {
    var value = parseInt(row.querySelector('[data-quick-order-qty]').value, 10);
    if (!isFinite(value) || value < 1) return 1;
    return value;
  };

  QuickOrderPad.prototype._renderRow = function (row) {
    var state = this.rowState.get(row) || {};
    var statusElement = row.querySelector('[data-quick-order-status]');
    var totalElement = row.querySelector('[data-quick-order-line-total]');
    var itemInput = row.querySelector('[data-quick-order-item]');

    statusElement.className = 'quick-order__status';
    statusElement.textContent = '';
    totalElement.textContent = '';
    row.classList.remove('is-invalid');
    itemInput.removeAttribute('aria-invalid');

    if (state.status === 'pending') {
      statusElement.classList.add('quick-order__status--pending');
      statusElement.textContent = this.string('resolving');
      return;
    }

    if (state.status === 'error') {
      statusElement.classList.add('quick-order__status--error');
      statusElement.textContent = this.string('lookupFailed');
      row.classList.add('is-invalid');
      itemInput.setAttribute('aria-invalid', 'true');
      return;
    }

    if (state.status === 'notfound' || state.status === 'ambiguous') {
      var isAmbiguous = state.status === 'ambiguous';
      statusElement.classList.add('quick-order__status--error');
      row.classList.add('is-invalid');
      itemInput.setAttribute('aria-invalid', 'true');

      var message = document.createElement('span');
      message.textContent = this.string(isAmbiguous ? 'ambiguous' : 'notFound') || this.string('notFound');
      statusElement.appendChild(message);

      var suggestions = state.suggestions || [];
      if (suggestions.length > 0) {
        statusElement.appendChild(document.createTextNode(' — ' + this.string(isAmbiguous ? 'pickOne' : 'didYouMean') + ' '));
        suggestions.forEach(function (variant, index) {
          if (index > 0) statusElement.appendChild(document.createTextNode(', '));
          var button = document.createElement('button');
          button.type = 'button';
          button.className = 'quick-order__suggestion';
          button.setAttribute('data-quick-order-suggestion', '');
          button.setAttribute('data-sku', variant.sku);
          if (isAmbiguous) button.setAttribute('data-pick', String(index));
          // Two candidates can carry the identical sku, so the label has to name the product
          // as well or the buyer is choosing between two identical buttons.
          button.textContent = isAmbiguous
            ? variant.sku + ' — ' + (variant.product_title || '')
                + (variant.variant_title && variant.variant_title !== 'Default Title' ? ' (' + variant.variant_title + ')' : '')
            : variant.sku;
          statusElement.appendChild(button);
        });
        statusElement.appendChild(document.createTextNode(isAmbiguous ? '' : '?'));
      }
      return;
    }

    if (state.status === 'resolved' && state.variant) {
      var variant = state.variant;
      var quantity = this._quantity(row);

      statusElement.classList.add(variant.available ? 'quick-order__status--found' : 'quick-order__status--warning');

      var name = document.createElement('span');
      name.className = 'quick-order__status-name';
      name.textContent = variant.product_title;
      statusElement.appendChild(name);

      if (variant.variant_title && variant.variant_title !== 'Default Title') {
        statusElement.appendChild(document.createTextNode(' · ' + variant.variant_title));
      }

      statusElement.appendChild(document.createTextNode(' · ' + (variant.price_formatted || formatMoney(variant.price)) + ' ea'));

      if (!variant.available) {
        statusElement.appendChild(document.createTextNode(' · '));
        var warning = document.createElement('span');
        warning.textContent = this.string('outOfStock');
        statusElement.appendChild(warning);
      }

      totalElement.textContent = formatMoney(variant.price * quantity);
    }
  };

  /** Rows that resolved to a real variant, in the order they appear. */
  QuickOrderPad.prototype._readyRows = function () {
    var self = this;
    return this._rows().filter(function (row) {
      var state = self.rowState.get(row);
      return state && state.status === 'resolved' && state.variant;
    });
  };

  QuickOrderPad.prototype._refreshSummary = function () {
    var self = this;
    var rows = this._rows();
    var entered = rows.filter(function (row) {
      return row.querySelector('[data-quick-order-item]').value.trim() !== '';
    });
    var ready = this._readyRows();

    var subtotal = ready.reduce(function (sum, row) {
      var state = self.rowState.get(row);
      return sum + (state.variant.price * self._quantity(row));
    }, 0);

    if (entered.length === 0) {
      this.summaryElement.textContent = this.string('summaryEmpty');
    } else {
      this.summaryElement.textContent = this.string('summaryCount', {
        '[READY]': String(ready.length),
        '[TOTAL]': String(entered.length),
        '[SUBTOTAL]': formatMoney(subtotal)
      });
    }

    var disabled = ready.length === 0;
    this.submitButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  };

  QuickOrderPad.prototype._setReport = function (content, tone) {
    if (!this.reportElement) return;

    this.reportElement.className = 'card__section quick-order__report';
    this.reportElement.textContent = '';

    if (!content) {
      this.reportElement.hidden = true;
      return;
    }

    if (tone) this.reportElement.classList.add('quick-order__report--' + tone);
    this.reportElement.hidden = false;

    if (typeof content === 'string') {
      this.reportElement.textContent = content;
      return;
    }

    this.reportElement.appendChild(content);
  };

  /* ----------------------------------------------------------------- paste */

  /**
   * Parse one pasted line into { sku, quantity, ambiguous }.
   *
   * A comma or a tab is unambiguous: everything before the last one is the item
   * number, everything after is the quantity. Whitespace is not, because item
   * numbers in this catalog contain spaces AND end in digits — "EXAM 4" is a whole
   * item number, "H5 DL 12" is an item number with a quantity. Those lines are
   * marked ambiguous and settled by asking the store: if the entire line resolves
   * to a real item number, it is one; otherwise the trailing digits are a quantity.
   */
  function parsePasteLine(line) {
    // Spreadsheet copy-paste often carries non-breaking spaces; treat them as spaces.
    var text = line.replace(/ /g, ' ').trim();
    if (!text) return null;

    var separator = Math.max(text.lastIndexOf(','), text.lastIndexOf('\t'), text.lastIndexOf(';'));
    if (separator > 0) {
      var head = text.slice(0, separator).trim();
      var tail = text.slice(separator + 1).trim();
      var parsed = parseInt(tail.replace(/[^\d]/g, ''), 10);
      if (head) {
        return { sku: head, quantity: isFinite(parsed) && parsed > 0 ? parsed : 1, ambiguous: false };
      }
    }

    var match = text.match(/^(.*\S)\s+(\d+)$/);
    if (match) {
      return {
        sku: text,
        fallbackSku: match[1],
        fallbackQuantity: parseInt(match[2], 10) || 1,
        quantity: 1,
        ambiguous: true
      };
    }

    return { sku: text, quantity: 1, ambiguous: false };
  }

  QuickOrderPad.prototype._applyPaste = function () {
    var self = this;
    if (!this.pasteField) return;

    var raw = this.pasteField.value || '';
    var lines = raw.split(/\r\n|\r|\n/).slice(0, MAX_PASTE_LINES);
    var parsed = lines.map(parsePasteLine).filter(Boolean);

    if (parsed.length === 0) return;

    this._setReport(this.string('resolving'));

    // Settle the ambiguous lines first, then lay the rows out in one pass so the
    // pad never flickers half-parsed.
    var settle = parsed.map(function (entry) {
      if (!entry.ambiguous) return Promise.resolve(entry);
      return self.lookup.lookup(entry.sku).then(function (result) {
        if (result.ok && result.exact) return { sku: entry.sku, quantity: entry.quantity };
        return { sku: entry.fallbackSku, quantity: entry.fallbackQuantity };
      });
    });

    return Promise.all(settle).then(function (entries) {
      // Reuse blank rows before appending, so a paste into a fresh pad does not
      // leave eight empty lines above the result.
      var available = self._rows().filter(function (row) {
        return row.querySelector('[data-quick-order-item]').value.trim() === '';
      });

      entries.forEach(function (entry) {
        var row = available.shift() || self._addRow(false);
        row.querySelector('[data-quick-order-item]').value = entry.sku;
        row.querySelector('[data-quick-order-qty]').value = String(entry.quantity);
      });

      self.pasteField.value = '';
      self._setReport(null);

      return Promise.all(self._rows().map(function (row) { return self._resolveRow(row); }));
    });
  };

  /* ------------------------------------------------------------------ cart */

  QuickOrderPad.prototype._submit = function () {
    var self = this;
    if (this.submitButton.getAttribute('aria-disabled') === 'true') {
      this._setReport(this.string('nothingToAdd'), 'error');
      return;
    }

    var rows = this._readyRows();
    if (rows.length === 0) {
      this._setReport(this.string('nothingToAdd'), 'error');
      return;
    }

    var lines = rows.map(function (row) {
      var state = self.rowState.get(row);
      return { row: row, id: state.variant.id, sku: state.variant.sku, quantity: self._quantity(row) };
    });

    this.submitButton.classList.add('is-loading');
    this._setReport(null);
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    // /cart/add.js with an items array is all-or-nothing: one rejected line and
    // nothing at all is added. That is the right default (no silent drops), but it
    // gives no way to tell WHICH line failed, so on rejection the lines are retried
    // one at a time. Because the batch was atomic, nothing was added before the
    // retry, so no line can be added twice.
    return this._addBatch(lines)
      .then(function (result) {
        if (result.ok) return { added: lines, rejected: [] };
        return self._addIndividually(lines);
      })
      .then(function (outcome) {
        self._finishSubmit(outcome);
      })
      .catch(function () {
        self._finishSubmit({ added: [], rejected: lines.map(function (line) {
          return { line: line, message: self.string('lookupFailed') };
        }) });
      });
  };

  QuickOrderPad.prototype._addBatch = function (lines) {
    return this._postToCart({
      items: lines.map(function (line) { return { id: line.id, quantity: line.quantity }; })
    }).then(function (response) {
      return { ok: response.ok };
    });
  };

  QuickOrderPad.prototype._addIndividually = function (lines) {
    var self = this;
    var added = [];
    var rejected = [];

    return lines.reduce(function (chain, line) {
      return chain.then(function () {
        return self._postToCart({ items: [{ id: line.id, quantity: line.quantity }] })
          .then(function (response) {
            if (response.ok) {
              added.push(line);
              return null;
            }
            return response.json().catch(function () { return {}; }).then(function (body) {
              rejected.push({ line: line, message: body.description || body.message || self.string('genericError') });
            });
          })
          .catch(function () {
            rejected.push({ line: line, message: self.string('genericError') });
          });
      });
    }, Promise.resolve()).then(function () {
      return { added: added, rejected: rejected };
    });
  };

  QuickOrderPad.prototype._postToCart = function (body) {
    return fetch(this.cartAddUrl + '.js', {
      body: JSON.stringify(body),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
  };

  QuickOrderPad.prototype._finishSubmit = function (outcome) {
    var self = this;
    this.submitButton.classList.remove('is-loading');
    document.dispatchEvent(new CustomEvent('theme:loading:end'));

    var addedCount = outcome.added.length;
    var addedQuantity = outcome.added.reduce(function (sum, line) { return sum + line.quantity; }, 0);

    if (addedCount > 0) {
      // Warehouse's documented hook: tells the mini-cart to re-render with the new
      // quantity (see assets/custom.js).
      document.documentElement.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true,
        detail: { quantity: addedQuantity }
      }));

      outcome.added.forEach(function (line) {
        self.rowState.delete(line.row);
        line.row.querySelector('[data-quick-order-item]').value = '';
        line.row.querySelector('[data-quick-order-qty]').value = '1';
        self._renderRow(line.row);
      });
    }

    var container = document.createElement('div');

    if (addedCount > 0) {
      var success = document.createElement('p');
      success.textContent = addedCount === 1
        ? this.string('addedOne')
        : this.string('addedMany', { '[COUNT]': String(addedCount) });
      container.appendChild(success);
    }

    if (outcome.rejected.length > 0) {
      var heading = document.createElement('p');
      heading.textContent = this.string('rejected');
      container.appendChild(heading);

      var list = document.createElement('ul');
      outcome.rejected.forEach(function (entry) {
        var item = document.createElement('li');
        item.textContent = entry.line.sku + ' — ' + entry.message;
        list.appendChild(item);
        entry.line.row.classList.add('is-invalid');
      });
      container.appendChild(list);
    }

    this._setReport(container, outcome.rejected.length > 0 ? 'error' : 'ok');
    this._refreshSummary();
  };

  /* ------------------------------------------------------------------ boot */

  function init() {
    document.querySelectorAll('[data-quick-order]').forEach(function (element) {
      if (element.__titanQuickOrder) return;
      element.__titanQuickOrder = new QuickOrderPad(element);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', init);
})();
